import { FC, useEffect, useRef, useState } from 'react';
import { FlexBox, MEDIA } from '@brg/ui';
import styled, { css } from 'styled-components';
import { API, Tag } from '@brg/api';
import { LayoutConst } from 'core';
import ReactTooltip from 'react-tooltip';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import hexToRGB from 'shared/helpers/hexToRGB';
import { DefaultTheme } from 'styled-components';
import { isSaleCard } from 'shared/helpers/isSaleCard';
const MAX_VISIBLE_ROWS = 2;

const RelatedWrapper = styled(FlexBox)`
  margin-top: 16px;
  margin-bottom: 16px;
  row-gap: 16px;

  @media (max-width: ${LayoutConst.screen.l}px) {
    margin-bottom: 0;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    margin-top: 16px;
  }
`;

const defaultScrollStyles = (scrollColor: string) => css`
  padding-right: 8px;

  &::-webkit-scrollbar {
    height: 1px;
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${scrollColor};
    border-radius: 3px;
  }

  @media ${MEDIA.TABLET_STRONG}, ${MEDIA.MOBILE_STRONG} {
    padding-right: 0;

    &::-webkit-scrollbar {
      height: 0;
      width: 0;
    }
  }
`;

const RelatedGridScrollMixin = (
  shadowColor?: string,
  maxHeight?: number,
  scrollColor?: string,
) => css`
  ${defaultScrollStyles(scrollColor || '#B6B6B5')};
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${maxHeight}px;

  &:after {
    content: '';
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 35px;
    display: ${shadowColor ? 'inline-block' : 'none'};
    pointer-events: none;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, ${shadowColor || ''} 100%);
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    overflow: visible;
    max-height: none;
    padding-right: 0;

    &:after {
      display: none;
    }
  }
`;

const RelatedGrid = styled(FlexBox)<{
  $canScroll?: boolean;
  $maxHeight?: number;
  $shadowColor?: string;
}>`
  column-gap: 16px;
  row-gap: 12px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    column-gap: 12px;
    row-gap: 10px;
  }

  ${({ theme, $canScroll, $maxHeight, $shadowColor }) =>
    $canScroll && $maxHeight
      ? RelatedGridScrollMixin(
          $shadowColor,
          $maxHeight,
          theme?.defaultScroll?.background,
        )
      : css``}
`;

const RelatedProjects = styled.div`
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 5px;
  background: #70706f;
  color: #fff;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  cursor: pointer;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

function getRealColorBg(element: HTMLElement | null): string {
  let el = element;

  while (el) {
    const bgColor = window.getComputedStyle(el).backgroundColor;
    if (bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
      return bgColor;
    }
    el = el.parentElement;
  }

  return 'rgb(254, 254, 254)';
}

function resolveShadowColor(container: HTMLElement, customColor?: string): string {
  if (customColor?.startsWith('#')) {
    return hexToRGB(customColor, 1);
  }

  if (customColor) {
    return customColor;
  }

  return getRealColorBg(container);
}

function isRelatedGridScrollEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth > LayoutConst.screen.s;
}

function measureRelatedGridScroll(container: HTMLElement) {
  const children = Array.from(container.children) as HTMLElement[];

  if (!children.length) {
    return { canScroll: false, maxHeight: undefined as number | undefined };
  }

  const rowTops = [...new Set(children.map((el) => el.offsetTop))].sort((a, b) => a - b);

  if (rowTops.length <= MAX_VISIBLE_ROWS) {
    return { canScroll: false, maxHeight: undefined };
  }

  const allowedTops = new Set(rowTops.slice(0, MAX_VISIBLE_ROWS));
  const containerTop = rowTops[0];
  let maxBottom = 0;

  children.forEach((el) => {
    if (allowedTops.has(el.offsetTop)) {
      maxBottom = Math.max(maxBottom, el.offsetTop + el.offsetHeight);
    }
  });

  return { canScroll: true, maxHeight: maxBottom - containerTop };
}

function useRelatedGridScroll(customShadowColor?: string, contentKey?: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<{
    canScroll: boolean;
    maxHeight?: number;
    shadowColor?: string;
  }>({ canScroll: false });

  useEffect(() => {
    const container = ref.current;
    if (!container || !contentKey) {
      setScrollState({ canScroll: false });
      return;
    }

    const update = () => {
      if (!isRelatedGridScrollEnabled()) {
        setScrollState((prev) =>
          prev.canScroll || prev.maxHeight || prev.shadowColor
            ? { canScroll: false }
            : prev,
        );
        return;
      }

      const { canScroll, maxHeight } = measureRelatedGridScroll(container);

      setScrollState((prev) => {
        const shadowColor = canScroll
          ? resolveShadowColor(container, customShadowColor)
          : undefined;

        if (
          prev.canScroll === canScroll &&
          prev.maxHeight === maxHeight &&
          prev.shadowColor === shadowColor
        ) {
          return prev;
        }

        return { canScroll, maxHeight, shadowColor };
      });
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);

    const handleViewportChange = () => update();
    window.addEventListener('resize', handleViewportChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [customShadowColor, contentKey]);

  return { ref, ...scrollState };
}

export interface RelatedProductsBlockProps {
  cards: any[];
  isReproduction?: any[];
  includedCollection?: any[];
  includedCalendar?: any[];
  isBookProject?: boolean;
  shadowColor?: string;
  theme: DefaultTheme;
  t: (key: string) => string;
  i18n: { language: string };
  handleOpenProductPage: (card: any) => string;
  handleOpenCardPage: () => void;
  handleOpenCollectionPage: () => void;
  handleOpenCalendarPage: () => void;
  formatSequenceNumber: (sequenceNumber?: string) => string;
}

export function hasRelatedProductsContent(
  props: Pick<
    RelatedProductsBlockProps,
    'cards' | 'isReproduction' | 'includedCollection' | 'includedCalendar'
  >,
): boolean {
  return !!(
    props.isReproduction?.length ||
    props.cards?.length ||
    props.includedCollection?.length ||
    props.includedCalendar?.length
  );
}

export const RelatedProductsBlock: FC<RelatedProductsBlockProps> = ({
  cards,
  isReproduction,
  includedCollection,
  includedCalendar,
  isBookProject,
  shadowColor,
  theme,
  t,
  i18n,
  handleOpenProductPage,
  handleOpenCardPage,
  handleOpenCollectionPage,
  handleOpenCalendarPage,
  formatSequenceNumber,
}) => {
  const hasContent = !!(
    isReproduction?.length ||
    cards?.length ||
    includedCollection?.length ||
    includedCalendar?.length
  );

  const contentKey = [
    cards?.length ?? 0,
    isReproduction?.length ?? 0,
    includedCollection?.length ?? 0,
    includedCalendar?.length ?? 0,
    i18n.language,
  ].join('-');

  const { ref, canScroll, maxHeight, shadowColor: gridShadowColor } = useRelatedGridScroll(
    shadowColor,
    hasContent ? contentKey : undefined,
  );

  if (!hasContent) {
    return null;
  }

  return (
    <RelatedWrapper direction="column">
      <RelatedGrid
        ref={ref}
        wrap="wrap"
        $canScroll={canScroll && isRelatedGridScrollEnabled()}
        $maxHeight={maxHeight}
        $shadowColor={gridShadowColor}
      >
        {!!cards?.length &&
          cards.map((card) => {
            const isCardForSale = isSaleCard(card);
            if (isCardForSale) {
              return;
            }
            return (
              <span key={card.cardId} data-tip={card.name} data-for={card.cardId}>
              <a href={handleOpenProductPage(card)} target="_blank" rel="noreferrer">
                <RelatedProjects>
                  {isBookProject
                    ? 'Электронная книга'
                    : `${t('buy_item')}${
                        API.toLang<Tag>(card.tags?.[0], i18n.language)?.name ||
                        formatSequenceNumber(card.sequenceItem?.number)
                      }`}
                </RelatedProjects>
              </a>
              <ReactTooltip
                {...getTooltipStyles(theme)}
                id={card.cardId}
                className={card.cardId}
              />
            </span>
            )
          })}
        {!!isReproduction?.length && (
          <span>
            <RelatedProjects onClick={handleOpenCardPage}>
              {`${t('buy_item')}${t('poster')}`}
            </RelatedProjects>
          </span>
        )}
        {!!includedCollection?.length && (
          <span>
            <RelatedProjects onClick={handleOpenCollectionPage}>
              {t('included_collection')}
            </RelatedProjects>
          </span>
        )}
        {!!includedCalendar?.length && (
          <span>
            <RelatedProjects onClick={handleOpenCalendarPage}>
              {`${t('buy_item')}${t('included_calendar')}`}
            </RelatedProjects>
          </span>
        )}
      </RelatedGrid>
    </RelatedWrapper>
  );
};
import { FC, useEffect, useRef, useState } from 'react';
import { FlexBox, MEDIA } from '@brg/ui';
import styled, { css } from 'styled-components';
import { API, Tag } from '@brg/api';
import { LayoutConst } from 'core';
import ReactTooltip from 'react-tooltip';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import hexToRGB from 'shared/helpers/hexToRGB';
import { DefaultTheme } from 'styled-components';
import { isSaleCard } from 'shared/helpers/isSaleCard';
const MAX_VISIBLE_ROWS = 2;

const RelatedWrapper = styled(FlexBox)`
  margin-top: 16px;
  margin-bottom: 16px;
  row-gap: 16px;

  @media (max-width: ${LayoutConst.screen.l}px) {
    margin-bottom: 0;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    margin-top: 16px;
  }
`;

const defaultScrollStyles = (scrollColor: string) => css`
  padding-right: 8px;

  &::-webkit-scrollbar {
    height: 1px;
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${scrollColor};
    border-radius: 3px;
  }

  @media ${MEDIA.TABLET_STRONG}, ${MEDIA.MOBILE_STRONG} {
    padding-right: 0;

    &::-webkit-scrollbar {
      height: 0;
      width: 0;
    }
  }
`;

const RelatedGridScrollMixin = (
  shadowColor?: string,
  maxHeight?: number,
  scrollColor?: string,
) => css`
  ${defaultScrollStyles(scrollColor || '#B6B6B5')};
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: ${maxHeight}px;

  &:after {
    content: '';
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 35px;
    display: ${shadowColor ? 'inline-block' : 'none'};
    pointer-events: none;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, ${shadowColor || ''} 100%);
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    overflow: visible;
    max-height: none;
    padding-right: 0;

    &:after {
      display: none;
    }
  }
`;

const RelatedGrid = styled(FlexBox)<{
  $canScroll?: boolean;
  $maxHeight?: number;
  $shadowColor?: string;
}>`
  column-gap: 16px;
  row-gap: 12px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    column-gap: 12px;
    row-gap: 10px;
  }

  ${({ theme, $canScroll, $maxHeight, $shadowColor }) =>
    $canScroll && $maxHeight
      ? RelatedGridScrollMixin(
          $shadowColor,
          $maxHeight,
          theme?.defaultScroll?.background,
        )
      : css``}
`;

const RelatedProjects = styled.div`
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 5px;
  background: #70706f;
  color: #fff;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  cursor: pointer;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    line-height: 16px;
  }
`;

function getRealColorBg(element: HTMLElement | null): string {
  let el = element;

  while (el) {
    const bgColor = window.getComputedStyle(el).backgroundColor;
    if (bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
      return bgColor;
    }
    el = el.parentElement;
  }

  return 'rgb(254, 254, 254)';
}

function resolveShadowColor(container: HTMLElement, customColor?: string): string {
  if (customColor?.startsWith('#')) {
    return hexToRGB(customColor, 1);
  }

  if (customColor) {
    return customColor;
  }

  return getRealColorBg(container);
}

function isRelatedGridScrollEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth > LayoutConst.screen.s;
}

function measureRelatedGridScroll(container: HTMLElement) {
  const children = Array.from(container.children) as HTMLElement[];

  if (!children.length) {
    return { canScroll: false, maxHeight: undefined as number | undefined };
  }

  const rowTops = [...new Set(children.map((el) => el.offsetTop))].sort((a, b) => a - b);

  if (rowTops.length <= MAX_VISIBLE_ROWS) {
    return { canScroll: false, maxHeight: undefined };
  }

  const allowedTops = new Set(rowTops.slice(0, MAX_VISIBLE_ROWS));
  const containerTop = rowTops[0];
  let maxBottom = 0;

  children.forEach((el) => {
    if (allowedTops.has(el.offsetTop)) {
      maxBottom = Math.max(maxBottom, el.offsetTop + el.offsetHeight);
    }
  });

  return { canScroll: true, maxHeight: maxBottom - containerTop };
}

function useRelatedGridScroll(customShadowColor?: string, contentKey?: string) {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<{
    canScroll: boolean;
    maxHeight?: number;
    shadowColor?: string;
  }>({ canScroll: false });

  useEffect(() => {
    const container = ref.current;
    if (!container || !contentKey) {
      setScrollState({ canScroll: false });
      return;
    }

    const update = () => {
      if (!isRelatedGridScrollEnabled()) {
        setScrollState((prev) =>
          prev.canScroll || prev.maxHeight || prev.shadowColor
            ? { canScroll: false }
            : prev,
        );
        return;
      }

      const { canScroll, maxHeight } = measureRelatedGridScroll(container);

      setScrollState((prev) => {
        const shadowColor = canScroll
          ? resolveShadowColor(container, customShadowColor)
          : undefined;

        if (
          prev.canScroll === canScroll &&
          prev.maxHeight === maxHeight &&
          prev.shadowColor === shadowColor
        ) {
          return prev;
        }

        return { canScroll, maxHeight, shadowColor };
      });
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);

    const handleViewportChange = () => update();
    window.addEventListener('resize', handleViewportChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [customShadowColor, contentKey]);

  return { ref, ...scrollState };
}

export interface RelatedProductsBlockProps {
  cards: any[];
  isReproduction?: any[];
  includedCollection?: any[];
  includedCalendar?: any[];
  isBookProject?: boolean;
  shadowColor?: string;
  theme: DefaultTheme;
  t: (key: string) => string;
  i18n: { language: string };
  handleOpenProductPage: (card: any) => string;
  handleOpenCardPage: () => void;
  handleOpenCollectionPage: () => void;
  handleOpenCalendarPage: () => void;
  formatSequenceNumber: (sequenceNumber?: string) => string;
}

export function hasRelatedProductsContent(
  props: Pick<
    RelatedProductsBlockProps,
    'cards' | 'isReproduction' | 'includedCollection' | 'includedCalendar'
  >,
): boolean {
  return !!(
    props.isReproduction?.length ||
    props.cards?.length ||
    props.includedCollection?.length ||
    props.includedCalendar?.length
  );
}

export const RelatedProductsBlock: FC<RelatedProductsBlockProps> = ({
  cards,
  isReproduction,
  includedCollection,
  includedCalendar,
  isBookProject,
  shadowColor,
  theme,
  t,
  i18n,
  handleOpenProductPage,
  handleOpenCardPage,
  handleOpenCollectionPage,
  handleOpenCalendarPage,
  formatSequenceNumber,
}) => {
  const hasContent = !!(
    isReproduction?.length ||
    cards?.length ||
    includedCollection?.length ||
    includedCalendar?.length
  );

  const contentKey = [
    cards?.length ?? 0,
    isReproduction?.length ?? 0,
    includedCollection?.length ?? 0,
    includedCalendar?.length ?? 0,
    i18n.language,
  ].join('-');

  const { ref, canScroll, maxHeight, shadowColor: gridShadowColor } = useRelatedGridScroll(
    shadowColor,
    hasContent ? contentKey : undefined,
  );

  if (!hasContent) {
    return null;
  }

  return (
    <RelatedWrapper direction="column">
      <RelatedGrid
        ref={ref}
        wrap="wrap"
        $canScroll={canScroll && isRelatedGridScrollEnabled()}
        $maxHeight={maxHeight}
        $shadowColor={gridShadowColor}
      >
        {!!cards?.length &&
          cards.map((card) => {
            const isCardForSale = isSaleCard(card);
            if (isCardForSale) {
              return;
            }
            return (
              <span key={card.cardId} data-tip={card.name} data-for={card.cardId}>
              <a href={handleOpenProductPage(card)} target="_blank" rel="noreferrer">
                <RelatedProjects>
                  {isBookProject
                    ? 'Электронная книга'
                    : `${t('buy_item')}${
                        API.toLang<Tag>(card.tags?.[0], i18n.language)?.name ||
                        formatSequenceNumber(card.sequenceItem?.number)
                      }`}
                </RelatedProjects>
              </a>
              <ReactTooltip
                {...getTooltipStyles(theme)}
                id={card.cardId}
                className={card.cardId}
              />
            </span>
            )
          })}
        {!!isReproduction?.length && (
          <span>
            <RelatedProjects onClick={handleOpenCardPage}>
              {`${t('buy_item')}${t('poster')}`}
            </RelatedProjects>
          </span>
        )}
        {!!includedCollection?.length && (
          <span>
            <RelatedProjects onClick={handleOpenCollectionPage}>
              {t('included_collection')}
            </RelatedProjects>
          </span>
        )}
        {!!includedCalendar?.length && (
          <span>
            <RelatedProjects onClick={handleOpenCalendarPage}>
              {`${t('buy_item')}${t('included_calendar')}`}
            </RelatedProjects>
          </span>
        )}
      </RelatedGrid>
    </RelatedWrapper>
  );
};
